import { getLeadIdFromSearch } from "./leadIdCookie";

const FRACTOLABS_APEX = "fractolabs.com";
const SETTRHQ_APEX = "settrhq.com";
const DEV_HOSTS = new Set(["localhost", "127.0.0.1"]);

function freezeBrand(brand) {
  Object.freeze(brand.logo);
  Object.freeze(brand.theme);
  Object.freeze(brand.analytics);
  Object.freeze(brand.links);
  Object.freeze(brand.social);
  return Object.freeze(brand);
}

export const BRANDS = {
  fractolabs: freezeBrand({
    id: "fractolabs",
    name: "FractoLabs",
    legalName: "FractoLabs, operated by Sofia Aleksejeva",
    address: "Tuulemaa 13-19, Tallinn, Estonia, 10312",
    governingLaw: "New South Wales, Australia",
    acn: "",
    abn: "",
    supportEmail: "support@fractolabs.com",
    infoEmail: "info@fractolabs.com",
    phone: "",
    canonicalOrigin: "https://fractolabs.com",
    locales: ["en"],
    ogLocale: "en_US",
    logo: {
      default: "/images/logo.png",
      onLight: "/images/logo_fractolabs_dark_gray.png",
    },
    favicon: "/favicon.ico",
    theme: {
      color: "#0B0F19",
      background: "#0F1115",
      accent: "#00D1FF",
    },
    analytics: {
      gtmId: "GTM-KTNX3NQX",
      posthogKey: "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs",
      posthogHost: "https://us.i.posthog.com",
    },
    links: { appStore: "", playStore: "" },
    social: {},
  }),
  settrhq: freezeBrand({
    id: "settrhq",
    name: "SettrHQ",
    legalName: "SettrHQ Pty Ltd",
    address: "",
    governingLaw: "",
    acn: "",
    abn: "",
    supportEmail: "support@settrhq.com",
    infoEmail: "info@settrhq.com",
    phone: "",
    canonicalOrigin: "https://settrhq.com",
    locales: ["en"],
    ogLocale: "en_US",
    logo: {
      default: "/images/logo_settrhq.png",
      onLight: "/images/logo_settrhq_dark_gray.png",
    },
    favicon: "/favicon.ico",
    theme: {
      color: "#0B0F19",
      background: "#0F1115",
      accent: "#00D1FF",
    },
    analytics: {
      gtmId: "GTM-KTNX3NQX",
      posthogKey: "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs",
      posthogHost: "https://us.i.posthog.com",
    },
    links: { appStore: "", playStore: "" },
    social: {},
  }),
};

export const HOST_BRAND = Object.freeze({
  "fractolabs.com": "fractolabs",
  "www.fractolabs.com": "fractolabs",
  "staging.fractolabs.com": "fractolabs",
  "settrhq.com": "settrhq",
  "www.settrhq.com": "settrhq",
  "staging.settrhq.com": "settrhq",
});

export const HOST_BRAND_EXCEPTIONS = new Set();

export function normalizeHostname(hostname) {
  return String(hostname || "").trim().toLowerCase().replace(/\.$/, "");
}

function isBrandId(value) {
  return Object.hasOwn(BRANDS, value);
}

function readDevBrandId(search, devBrand) {
  const params = new URLSearchParams(search);
  if (params.has("brand")) {
    const value = String(params.get("brand") || "").trim().toLowerCase();
    return isBrandId(value) ? value : "fractolabs";
  }
  const fromEnv = String(devBrand || "").trim().toLowerCase();
  return isBrandId(fromEnv) ? fromEnv : "fractolabs";
}

function inheritedBrandId(hostname) {
  const labels = hostname.split(".");
  if (labels.length !== 3 || labels.some((label) => label.length === 0)) {
    return null;
  }
  const apex = `${labels[1]}.${labels[2]}`;
  if (apex === FRACTOLABS_APEX) return "fractolabs";
  if (apex === SETTRHQ_APEX) return "settrhq";
  return null;
}

export function resolveBrandId(
  hostname,
  search = "",
  exceptions = HOST_BRAND_EXCEPTIONS,
  devBrand = process.env.REACT_APP_DEV_BRAND,
) {
  const host = normalizeHostname(hostname);
  if (!host) return null;
  if (DEV_HOSTS.has(host)) {
    return readDevBrandId(search, devBrand);
  }
  if (Object.hasOwn(HOST_BRAND, host)) {
    return HOST_BRAND[host];
  }
  if (exceptions.has(host)) {
    return null;
  }
  return inheritedBrandId(host);
}

export function getBrand(
  hostname,
  search = "",
  exceptions = HOST_BRAND_EXCEPTIONS,
  devBrand = process.env.REACT_APP_DEV_BRAND,
) {
  const id = resolveBrandId(hostname, search, exceptions, devBrand);
  return id ? BRANDS[id] : null;
}

function leadRedirectPath(pathname) {
  const path = String(pathname || "");
  return path.startsWith("/") ? path : `/${path}`;
}

function leadRedirectSearch(search) {
  if (!search) return "";
  const query = String(search);
  return query.startsWith("?") ? query : `?${query}`;
}

export function unknownHostLeadRedirectUrl(pathname, search) {
  if (!getLeadIdFromSearch(search)) return null;
  const origin = BRANDS.fractolabs.canonicalOrigin.replace(/\/$/, "");
  return `${origin}${leadRedirectPath(pathname)}${leadRedirectSearch(search)}`;
}

export function formatCopyright(brand, year = new Date().getFullYear()) {
  return `© ${year} ${brand.legalName}`;
}
